import { Query, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  MondayBatchJobStateJobStatus,
  MondayBatchJobStatesDTO,
} from "generated/schemas";
import { touchpointKeys } from "hooks/api/query-keys";
import { useCampaignJobStates } from "hooks/api/use-touchpoints";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";

const INITIAL_POLLING_INTERVAL = 5000;
const MEDIUM_POLLING_INTERVAL = 10000;
const MAX_POLLING_INTERVAL = 30000;

const INITIAL_BACKOFF_THRESHOLD_MS = 15000;
const MEDIUM_BACKOFF_THRESHOLD_MS = 45000;

/**
 * Hook to handle polling for campaign Monday job states with backoff logic.
 * Touchpoint list/detail are only invalidated once job states leave RUNNING
 * (SUCCESSFUL / FAILED / absent) — not when touchpoint RUNNING clears on its own.
 * @param shouldPollJobs - Whether Monday job-states polling should be enabled.
 */
export const useCampaignJobPolling = (shouldPollJobs: boolean) => {
  const queryClient = useQueryClient();
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const [pollingStartTime, setPollingStartTime] = useState<number | null>(null);
  const prevHasRunningJobsRef = useRef(false);

  const calculatePollingInterval = useCallback(
    (query: Query<MondayBatchJobStatesDTO, Error, MondayBatchJobStatesDTO, any>) => {
      const hasRunningJobs = query.state.data?.states.some(
        (job) => job.jobStatus === MondayBatchJobStateJobStatus.RUNNING,
      );

      if (query.state.status === "error" || !hasRunningJobs) {
        if (pollingStartTime !== null) setPollingStartTime(null);
        return false;
      }

      const startTime = pollingStartTime ?? Date.now();
      if (pollingStartTime === null) setPollingStartTime(startTime);

      const elapsed = Date.now() - startTime;

      if (elapsed < INITIAL_BACKOFF_THRESHOLD_MS) return INITIAL_POLLING_INTERVAL;
      if (elapsed < MEDIUM_BACKOFF_THRESHOLD_MS) return MEDIUM_POLLING_INTERVAL;
      return MAX_POLLING_INTERVAL;
    },
    [pollingStartTime],
  );

  const { data: jobStates } = useCampaignJobStates({
    refetchInterval: calculatePollingInterval,
    refetchOnWindowFocus: false,
    enabled: shouldPollJobs,
  });

  const hasRunningJobs = jobStates?.states.some(
    (job) => job.jobStatus === MondayBatchJobStateJobStatus.RUNNING,
  );

  useEffect(() => {
    const hadRunningJobs = prevHasRunningJobsRef.current;
    // Wait until Monday job status is no longer RUNNING (completed or failed)
    // before refreshing touchpoints — avoid refetching on every poll tick.
    const pollingJustFinished = hadRunningJobs && !hasRunningJobs;

    if (pollingJustFinished) {
      queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: touchpointKeys.jobStates(currentClient.id, currentCampaign.id),
      });
    }

    prevHasRunningJobsRef.current = !!hasRunningJobs;
  }, [
    currentCampaign.id,
    currentClient.id,
    hasRunningJobs,
    queryClient,
  ]);

  const startPolling = useCallback(() => {
    setPollingStartTime(Date.now());
    queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });
    queryClient.invalidateQueries({
      queryKey: touchpointKeys.jobStates(currentClient.id, currentCampaign.id),
    });
  }, [currentClient.id, currentCampaign.id, queryClient]);

  return {
    jobStates,
    hasRunningJobs,
    startPolling,
  };
};
